import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Activity, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between border-r border-border p-10 relative overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <Link to="/" className="relative flex items-center gap-2">
        <Activity className="h-4 w-4 text-primary" />
        <span className="font-display font-bold tracking-tight">DevOps Mentor AI</span>
      </Link>
      <div className="relative max-w-md">
        <h2 className="text-3xl font-display font-black tracking-tighter leading-none">
          From writing features to owning production.
        </h2>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          Assessment-driven roadmaps, AI review on every artifact, and a readiness report built from
          evidence rather than course completion badges.
        </p>
      </div>
      <p className="relative font-mono text-xs text-muted-foreground">$ whoami → future platform engineer</p>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-10">
      <div className="w-full max-w-sm fade-up">
        <h1 className="text-2xl font-display font-bold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
        <div className="mt-8">{children}</div>
        {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
      </div>
    </div>
  </div>
);

export const ErrorNote = ({ message }) =>
  message ? (
    <p data-testid="auth-error" className="text-sm text-destructive border border-destructive/40 bg-destructive/5 px-3 py-2">
      {message}
    </p>
  ) : null;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(email, password);
      navigate(location.state?.from || (user?.onboardingComplete ? "/dashboard" : "/onboarding"), { replace: true });
    } catch (err) {
      setError(errMsg(err, "Unable to sign in"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Continue your DevOps track."
      footer={
        <>
          No account?{" "}
          <Link to="/register" className="text-primary hover:underline" data-testid="link-register">Start free</Link>
          {" · "}
          <Link to="/forgot-password" className="text-primary hover:underline" data-testid="link-forgot">Forgot password</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <ErrorNote message={error} />
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none" placeholder="you@company.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none" placeholder="••••••••" />
        </div>
        <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full rounded-none gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
